import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CreditCardOutlined,
    ExclamationCircleFilled,
    FileExcelOutlined,
    FilePdfOutlined,
    MessageOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    Badge,
    Button,
    Card,
    Col,
    ConfigProvider,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import frFR from "antd/locale/fr_FR";
import { createRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Gabarit PDF partagé des états PoolIns (en-tête/pied, logos, pagination).
import {
    NAVY, drawEtatHeaderFooter, finalizeEtatPdf, loadEntete, loadLogos, loadLogoCompagnieConnectee,
} from "../stats/sinistre/etatsSinistresFunctions";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate } from "../../variables/functions";
import RecuCurrentFormat from "../production/components/docs/RecuCurrentFormat";
import { displayNumbers } from "../../variables/numbersFunctions";
import dayjs from "dayjs";

const { confirm } = Modal;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const STATUS_COLORS = {
    paid: { bg: "#f6ffed", border: "#b7eb8f", text: "#389e0d" },
    pending: { bg: "#fffbe6", border: "#ffe58f", text: "#d48806" },
};

// Période par défaut = mois courant. La fonction PG /api/reglement balaie tout le portefeuille
// quand aucune date n'est passée → chargement non borné qui continue en arrière-plan et finit en
// 504. En bornant la 1re requête au mois en cours, l'affichage est immédiat ; l'utilisateur élargit
// la période s'il a besoin de l'historique.
const DEFAULT_MONTH_RANGE = [dayjs().startOf("month"), dayjs().endOf("month")];
const DEFAULT_MONTH_STRINGS = [
    DEFAULT_MONTH_RANGE[0].format("YYYY-MM-DD"),
    DEFAULT_MONTH_RANGE[1].format("YYYY-MM-DD"),
];

// lockStatut : 'PAID' (v2 « encaissés uniquement ») | 'UNPAID' | null (écran complet historique)
const Encaissements = ({ lockStatut = null } = {}) => {
    const [datas, setDatas] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadingData, setLoadingData] = useState(true);
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [modalModePaiement, setModalModePaiement] = useState(false);
    const [contractToPay, setContractToPay] = useState("");
    const [numeroPaiement, setNumeroPaiement] = useState("P");
    const [modePaiement, setModePaiement] = useState("");
    const [modePaiementForm] = Form.useForm();
    const [banques, setBanques] = useState([]);
    const [banque, setBanque] = useState("BANQUE");
    const [dateF, setDateF] = useState(DEFAULT_MONTH_STRINGS);
    const [reglement, setReglement] = useState("");
    const [recuContrat, setRecuContrat] = useState(null);
    const [recuReglement, setRecuReglement] = useState(null);
    const [loadingRecu, setLoadingRecu] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [useCompensationRistourne, setUseCompensationRistourne] = useState(false);
    const [havingRistourne, setHavingRistourne] = useState(false);
    const [count, setCount] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingEtat, setExportingEtat] = useState(false);
    const [dateVirement, setDateVirement] = useState(null);
    const [reference, setReference] = useState("");
    // Encaissement PARTIEL (sous habilitation HLA093) : montant réellement encaissé (≤ dû).
    const [montantEncaisse, setMontantEncaisse] = useState(null);
    const canEncaissementPartiel = rightsService("HLA093");
    // Complétion d'un encaissement partiel.
    const [completModal, setCompletModal] = useState(false);
    const [completReglement, setCompletReglement] = useState(null);
    const [completMontant, setCompletMontant] = useState(null);
    const [searchText, setSearchText] = useState("");
    // Recherche + filtres pilotés CÔTÉ SERVEUR (la liste est paginée par la fonction PG) :
    // ils portent désormais sur l'ensemble des règlements, pas sur la seule page courante.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statutFilter, setStatutFilter] = useState(lockStatut);     // null | 'PAID' | 'UNPAID'
    const [modeFilter, setModeFilter] = useState(null);         // null | ordinal TypePaiement
    // Filtre par CAISSIER : code_id de l'utilisateur ayant encaissé (null = tous).
    const [caissierFilter, setCaissierFilter] = useState(null);
    const [caissiers, setCaissiers] = useState([]);

    const navigate = useNavigate();

    // Ordinaux TypePaiement (cf. resolveModePaiementLabel côté backend).
    const MODE_ORDINAL = { ESPECES: 0, BANQUE: 1, MOBILE_MONEY: 2, RISTOURNE: 3, COMPENSATION: 4, VIREMENT: 5, CHEQUE: 6 };

    const isPaid = (record) =>
        !!(record.modePaiement && record.modePaiement !== "-" && record?.dateReglement);

    const sortedDatas = [...datas].sort((a, b) => {
        const aPaid = isPaid(a) ? 1 : 0;
        const bPaid = isPaid(b) ? 1 : 0;
        return aPaid - bPaid;
    });

    // La recherche est désormais côté serveur (param `search`) → pas de re-filtrage client.
    const filteredDatas = sortedDatas;

    // Debounce de la saisie → param serveur `search` ; retour à la page 1 à chaque changement.
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(searchText.trim());
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    // Filtres de colonnes (Statut / Mode de paiement) → params serveur statut/modePaiement.
    const handleTableChange = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);

        const sSel = filters?.statut?.[0];
        let statut = sSel === "paid" ? "PAID" : sSel === "pending" ? "UNPAID"
            : sSel === "partial" ? "PARTIAL" : null;

        const mSel = filters?.modePaiement?.[0];
        let mode = null;
        if (mSel === "-") {
            statut = "UNPAID"; // « Non encaissé » choisi via la colonne Mode de paiement
        } else if (mSel != null && MODE_ORDINAL[mSel] != null) {
            mode = MODE_ORDINAL[mSel];
            statut = "PAID";
        }

        // Vue verrouillée (v2) : le statut reste figé quoi que choisisse l'utilisateur.
        if (lockStatut) {
            statut = lockStatut;
        }

        const filtersChanged = statut !== statutFilter || mode !== modeFilter;
        setStatutFilter(statut);
        setModeFilter(mode);
        setCurrentPage(filtersChanged ? 1 : (pagination.current || 1));
        setPageSize(pagination.pageSize || 10);
    };

    // Carte « Non encaissés » → liste des règlements non encaissés uniquement.
    const filtrerNonEncaisses = () => {
        setStatutFilter("UNPAID");
        setModeFilter(null);
        setFilteredInfo((prev) => ({ ...prev, statut: ["pending"], modePaiement: null }));
        setCurrentPage(1);
    };

    useEffect(() => {
        setLoadingData(true);
        axiosInstance
            .get(`/api/reglement`, {
                params: {
                    page: currentPage - 1,
                    size: pageSize,
                    search: debouncedSearch || "",
                    startDate: dateF?.[0] || null,
                    endDate: dateF?.length > 1 ? dateF[1] : null,
                    compagnie: "",
                    // lockStatut (v2) prévaut → la vue reste verrouillée sur les encaissés.
                    statut: lockStatut || statutFilter || null,
                    modePaiement: modeFilter,
                    caissier: caissierFilter || null,
                },
            })
            .then((res) => {
                setDatas(res.data.content);
                setTotal(res.data.totalElements);
                setLoadingData(false);
            })
            .catch((err) => {
                console.log(err);
                setLoadingData(false);
            });

        axiosInstance.get(`/api/reglement/encaissements-count`).then((res) => setCount(res.data)).catch(console.log);
        axiosInstance.get(`/api/parametre/type/BANQUE`).then((res) => setBanques(res.data)).catch(console.log);
    }, [currentPage, pageSize, dateF, debouncedSearch, statutFilter, modeFilter, caissierFilter]);

    // Caissiers disponibles pour le filtre (périmètre de l'utilisateur, côté serveur).
    useEffect(() => {
        axiosInstance.get(`/api/reglement/caissiers`)
            .then((res) => setCaissiers(Array.isArray(res.data) ? res.data : []))
            .catch(console.log);
    }, []);

    const encaisserContrat = (record) => {
        axiosInstance
            .get(`/api/reglement/check-ristourne/${record.codeId}`)
            .then((resp) => setHavingRistourne(resp.data))
            .catch(console.log);
        setModalModePaiement(true);
        setContractToPay(record);
        // Par défaut : encaissement PLEIN (montant encaissé = dû). Modifiable sous HLA093.
        setMontantEncaisse(record?.montant ?? null);
    };

    const exportToExcel = async () => {
        // L'export porte sur TOUTES les données correspondant aux filtres (période, recherche,
        // statut, mode). Le fichier .xlsx est désormais généré et streamé par le serveur
        // (/export/excel) : plus de requête size=100000 ni de construction côté navigateur
        // (l'ancien schéma saturait → lenteur puis 500).
        setExporting(true);
        try {
            const response = await axiosInstance.get(`/api/reglement/export/excel`, {
                params: {
                    search: debouncedSearch || "",
                    startDate: dateF?.[0] || null,
                    endDate: dateF?.length > 1 ? dateF[1] : null,
                    compagnie: "",
                    statut: statutFilter || null,
                    modePaiement: modeFilter,
                    caissier: caissierFilter || null,
                },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            // Caissier filtré → il apparaît dans le NOM du fichier (et en en-tête du classeur,
            // cf. backend) : sans cela deux exports de périmètres différents seraient indiscernables.
            const nomCaissier = caissierFilter
                ? (caissiers.find((c) => c.codeId === caissierFilter)?.nom || caissierFilter)
                : null;
            const suffixe = nomCaissier ? `_${nomCaissier.replace(/[^\w-]+/g, "_")}` : "";
            link.setAttribute("download", `encaissements${suffixe}_${dayjs().format("YYYY-MM-DD")}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de l'export. Réessayez, ou réduisez la période sélectionnée.");
        } finally {
            setExporting(false);
        }
    };

    // Nom du caissier filtré (null si « tous ») — sert aux titres et aux noms de fichiers.
    const nomCaissierFiltre = () => (caissierFilter
        ? (caissiers.find((c) => c.codeId === caissierFilter)?.nom || caissierFilter)
        : null);

    // Libellé de la période sélectionnée, pour les sous-titres des états.
    const libellePeriode = () => (dateF?.length === 2
        ? `du ${dayjs(dateF[0]).format("DD/MM/YYYY")} au ${dayjs(dateF[1]).format("DD/MM/YYYY")}`
        : "toutes périodes");

    /**
     * Récupère TOUTES les lignes correspondant aux filtres courants (au-delà de la page
     * affichée) : les états PDF doivent porter sur l'ensemble, comme l'export Excel.
     */
    const fetchToutesLesLignes = async () => {
        const res = await axiosInstance.get(`/api/reglement`, {
            params: {
                page: 0,
                size: 100000,
                search: debouncedSearch || "",
                startDate: dateF?.[0] || null,
                endDate: dateF?.length > 1 ? dateF[1] : null,
                compagnie: "",
                statut: lockStatut || statutFilter || null,
                modePaiement: modeFilter,
                caissier: caissierFilter || null,
            },
        });
        return res.data?.content || [];
    };

    // Séparateur de milliers : NE PAS utiliser toLocaleString("fr-FR") — il produit une espace
    // insécable étroite (U+202F) que jsPDF ne sait pas rendre et affiche « / » (« 286/165 »).
    // Pattern éprouvé du projet : format en-DE (points séparateurs) puis espace ASCII.
    const pdfMontant = (v) => new Intl.NumberFormat("en-DE").format(Math.trunc(Number(v) || 0)).replace(/\./g, " ");

    /** Export PDF « à plat » — pendant exact de l'export Excel, avec ligne de totaux. */
    const exportToPDF = async () => {
        setExportingPdf(true);
        try {
            const lignes = await fetchToutesLesLignes();
            if (!lignes.length) {
                toast.info("Aucun encaissement à exporter pour ces filtres.");
                return;
            }
            const doc = new jsPDF({ orientation: "landscape" });
            let enteteData = null, logos = [], logoCompagnie = null;
            // Logo de la compagnie de l'utilisateur : entête à 2 logos (compagnie à gauche,
            // Pool à droite), comme les Conditions Particulières. null pour un user du Pool.
            try { [enteteData, logos, logoCompagnie] = await Promise.all(
                [loadEntete(), loadLogos(), loadLogoCompagnieConnectee()]); } catch (e) { /* optionnels */ }
            const formattedNow = new Date().toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            });

            const head = [["N°", "N° Reçu", "Assuré", "N° Police", "Av.", "Montant", "Mode", "Date règlement", "Caissier", "Agence"]];
            const body = lignes.map((r, i) => [
                i + 1,
                r.numeroRecu || "-",
                `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`.trim() || "-",
                r.numeroPolice || "-",
                r.avenant || "AN",
                pdfMontant(r.montant),
                (r.modePaiement && r.modePaiement !== "-") ? r.modePaiement : "Non encaissé",
                r.dateReglement ? dayjs(r.dateReglement).format("DD/MM/YYYY") : "-",
                r.caissierNom || "-",
                r.agenceCaissier || "-",
            ]);
            const total = lignes.reduce((s, r) => s + (Number(r.montant) || 0), 0);
            const foot = [[
                { content: `TOTAL (${lignes.length} encaissement(s))`, colSpan: 5, styles: { halign: "right", fontStyle: "bold" } },
                { content: pdfMontant(total), styles: { halign: "right", fontStyle: "bold" } },
                "", "", "", "",
            ]];

            autoTable(doc, {
                head, body, foot,
                theme: "grid",
                styles: { fontSize: 7, cellPadding: 1.3, overflow: "linebreak", valign: "middle" },
                headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7, fontStyle: "bold" },
                footStyles: { fillColor: [243, 244, 246], textColor: 20 },
                alternateRowStyles: { fillColor: [246, 247, 251] },
                margin: { top: 32, bottom: 22, left: 10, right: 10 },
                columnStyles: {
                    0: { cellWidth: 10, halign: "center" },
                    1: { cellWidth: 24 },
                    5: { halign: "right" },
                    7: { halign: "center" },
                },
                didDrawPage: () => drawEtatHeaderFooter(doc, {
                    title: "ÉTAT DES ENCAISSEMENTS",
                    subtitle: `${libellePeriode()}${nomCaissierFiltre() ? ` · Caissier : ${nomCaissierFiltre()}` : ""}`,
                    enteteData, logos, formattedNow, logoCompagnie,
                }),
            });
            finalizeEtatPdf(doc);
            const suffixe = nomCaissierFiltre() ? `_${nomCaissierFiltre().replace(/[^\w-]+/g, "_")}` : "";
            doc.save(`encaissements${suffixe}_${dayjs().format("YYYY-MM-DD")}.pdf`);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de l'export PDF. Réessayez, ou réduisez la période sélectionnée.");
        } finally {
            setExportingPdf(false);
        }
    };

    /**
     * ÉTAT JOURNALIER : encaissements groupés par AGENCE puis par CAISSIER, avec sous-total à
     * chaque rupture et total général. Axe = DATE DE RÈGLEMENT (l'entrée réelle en caisse).
     * Les règlements non encaissés sont exclus : un état de caisse ne porte que sur l'encaissé.
     */
    const genererEtatJournalier = async () => {
        setExportingEtat(true);
        try {
            const brutes = await fetchToutesLesLignes();
            const lignes = brutes.filter((r) => r.dateReglement && r.modePaiement && r.modePaiement !== "-");
            if (!lignes.length) {
                toast.info("Aucun encaissement sur la période sélectionnée.");
                return;
            }

            // Regroupement agence → caissier, tri alphabétique pour un rendu stable.
            const groupes = new Map();
            lignes.forEach((r) => {
                const ag = r.agenceCaissier || "Agence non renseignée";
                const ca = r.caissierNom || "Caissier non renseigné";
                if (!groupes.has(ag)) groupes.set(ag, new Map());
                const parCaissier = groupes.get(ag);
                if (!parCaissier.has(ca)) parCaissier.set(ca, []);
                parCaissier.get(ca).push(r);
            });

            const doc = new jsPDF({ orientation: "portrait" });
            let enteteData = null, logos = [], logoCompagnie = null;
            // Logo de la compagnie de l'utilisateur : entête à 2 logos (compagnie à gauche,
            // Pool à droite), comme les Conditions Particulières. null pour un user du Pool.
            try { [enteteData, logos, logoCompagnie] = await Promise.all(
                [loadEntete(), loadLogos(), loadLogoCompagnieConnectee()]); } catch (e) { /* optionnels */ }
            const formattedNow = new Date().toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            });

            const body = [];
            let totalGeneral = 0;
            let nbGeneral = 0;
            [...groupes.keys()].sort().forEach((ag) => {
                const parCaissier = groupes.get(ag);
                let totalAgence = 0;
                let nbAgence = 0;
                // Rupture AGENCE
                body.push([{ content: `AGENCE : ${ag}`, colSpan: 6,
                    styles: { fillColor: NAVY, textColor: 255, fontStyle: "bold" } }]);

                [...parCaissier.keys()].sort().forEach((ca) => {
                    const rows = parCaissier.get(ca);
                    const totalCaissier = rows.reduce((s, r) => s + (Number(r.montant) || 0), 0);
                    totalAgence += totalCaissier; nbAgence += rows.length;

                    // Rupture CAISSIER
                    body.push([{ content: `Caissier : ${ca}`, colSpan: 6,
                        styles: { fillColor: [232, 234, 246], fontStyle: "bold" } }]);
                    rows.forEach((r) => body.push([
                        r.dateReglement ? dayjs(r.dateReglement).format("DD/MM/YYYY") : "-",
                        r.numeroRecu || "-",
                        r.numeroPolice || "-",
                        `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`.trim() || "-",
                        r.modePaiement || "-",
                        { content: pdfMontant(r.montant), styles: { halign: "right" } },
                    ]));
                    body.push([
                        { content: `Sous-total ${ca} (${rows.length})`, colSpan: 5,
                            styles: { halign: "right", fontStyle: "bold" } },
                        { content: pdfMontant(totalCaissier), styles: { halign: "right", fontStyle: "bold" } },
                    ]);
                });

                body.push([
                    { content: `TOTAL AGENCE ${ag} (${nbAgence})`, colSpan: 5,
                        styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                    { content: pdfMontant(totalAgence),
                        styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                ]);
                totalGeneral += totalAgence; nbGeneral += nbAgence;
            });

            autoTable(doc, {
                head: [["Date", "N° Reçu", "N° Police", "Assuré", "Mode", "Montant"]],
                body,
                foot: [[
                    { content: `TOTAL GÉNÉRAL (${nbGeneral} encaissement(s))`, colSpan: 5,
                        styles: { halign: "right", fontStyle: "bold" } },
                    { content: pdfMontant(totalGeneral), styles: { halign: "right", fontStyle: "bold" } },
                ]],
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 1.6, overflow: "linebreak", valign: "middle" },
                headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, fontStyle: "bold" },
                footStyles: { fillColor: [243, 244, 246], textColor: 20 },
                margin: { top: 32, bottom: 22, left: 14, right: 14 },
                columnStyles: { 0: { cellWidth: 22, halign: "center" }, 1: { cellWidth: 26 }, 5: { cellWidth: 28, halign: "right" } },
                didDrawPage: () => drawEtatHeaderFooter(doc, {
                    title: "ÉTAT JOURNALIER DES ENCAISSEMENTS",
                    subtitle: `Par agence et par caissier · ${libellePeriode()}`,
                    enteteData, logos, formattedNow, logoCompagnie,
                }),
            });
            finalizeEtatPdf(doc);
            const jour = dateF?.length === 2 ? dayjs(dateF[0]).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
            doc.save(`etat_journalier_encaissements_${jour}.pdf`);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la génération de l'état journalier.");
        } finally {
            setExportingEtat(false);
        }
    };

    const columns = [
        {
            title: "#",
            key: "index",
            width: 50,
            render: (_, __, index) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {(currentPage - 1) * pageSize + index + 1}
                </Text>
            ),
        },
        {
            title: "Statut",
            key: "statut",
            width: 130,
            render: (_, record) =>
                isPaid(record) ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 6 }}>
                        Encaissé
                    </Tag>
                ) : (
                    <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 6 }}>
                        En attente
                    </Tag>
                ),
            filters: [
                { text: "En attente", value: "pending" },
                { text: "Encaissé", value: "paid" },
                ...(canEncaissementPartiel ? [{ text: "Partiels (reste à payer)", value: "partial" }] : []),
            ],
            filterMultiple: false,
            filteredValue: filteredInfo.statut || null,
            // Filtrage serveur (cf. handleTableChange) — pas de onFilter client.
        },
        {
            // N° de reçu (quittance) : à reporter dans le fichier journalier de la comptabilité.
            // Vide sur les encaissements antérieurs à la fonctionnalité.
            title: "N° Reçu",
            dataIndex: "numeroRecu",
            key: "numeroRecu",
            width: 140,
            render: (v) => v
                ? <Text copyable style={{ fontSize: 12, whiteSpace: "nowrap" }}>{v}</Text>
                : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
        },
        {
            title: "Assuré",
            key: "assure",
            width: 220,
            render: (_, record) => (
                <div>
                    <Text strong style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "normal" }}>
                        {record.nomSouscripteur}{record.prenomSouscripteur ? ` ${record.prenomSouscripteur}` : ""}
                    </Text>
                    <div>
                        <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{record.numeroPolice}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Avenant",
            dataIndex: "avenant",
            key: "avenant",
            width: 100,
            render: (_, record) => (
                <Tag color="blue" style={{ borderRadius: 4 }}>
                    {record.avenant || "AN"}
                </Tag>
            ),
        },
        {
            title: "Montant",
            dataIndex: "montant",
            key: "montant",
            width: 150,
            sorter: (a, b) => a.montant - b.montant,
            // Le « Reste à payer » (encaissement partiel HLA093) est FACTORISÉ ici sous forme
            // de tag sous le montant, uniquement s'il reste quelque chose à payer → une colonne
            // dédiée en moins, plus de scroll horizontal.
            render: (_, record) => {
                const reste = canEncaissementPartiel
                    ? (record.resteAPayer != null
                        ? record.resteAPayer
                        : Math.max((record.montant || 0) - (record.montantPaye != null ? record.montantPaye : (record.montant || 0)), 0))
                    : 0;
                return (
                    <div>
                        <Text strong style={{ color: isPaid(record) ? "#389e0d" : "#d48806", fontSize: 13 }}>
                            {displayNumbers(record.montant)} <span style={{ fontWeight: 400, fontSize: 11 }}>FCFA</span>
                        </Text>
                        {reste > 0 && (
                            <div style={{ marginTop: 2 }}>
                                <Tag color="volcano" style={{ borderRadius: 4, fontSize: 11, margin: 0 }}>
                                    Reste {displayNumbers(Math.trunc(reste))} FCFA
                                </Tag>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Caissier",
            dataIndex: "caissierNom",
            key: "caissierNom",
            width: 180,
            // Vide sur la reprise de données et les règlements antérieurs au champ caissier.
            // L'OBSERVATION d'encaissement est FACTORISÉE ici : icône 💬 + tooltip au survol
            // quand elle existe (plus de colonne « Observation » dédiée).
            render: (v, record) => {
                const nom = v
                    ? <Text style={{ fontSize: 12 }}>{v}</Text>
                    : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                if (!record.observation) return nom;
                return (
                    <Space size={4}>
                        {nom}
                        <Tooltip title={record.observation}>
                            <MessageOutlined style={{ color: "#1677ff", fontSize: 13, cursor: "pointer" }} />
                        </Tooltip>
                    </Space>
                );
            },
        },
        {
            title: "Mode de paiement",
            dataIndex: "modePaiement",
            key: "modePaiement",
            width: 160,
            render: (_, record) => {
                if (!isPaid(record)) return <Text type="secondary">—</Text>;
                const modeColors = {
                    ESPECES: "green", ESPECE: "green",
                    BANQUE: "blue", VIREMENT: "blue",
                    MOBILE_MONEY: "purple", RISTOURNE: "gold",
                };
                return (
                    <Tag color={modeColors[record.modePaiement] || "default"} style={{ borderRadius: 4 }}>
                        {record.modePaiement}
                    </Tag>
                );
            },
            filters: [
                { text: "Espèces", value: "ESPECES" },
                { text: "Banque", value: "BANQUE" },
                { text: "Virement", value: "VIREMENT" },
                { text: "Mobile Money", value: "MOBILE_MONEY" },
                { text: "Ristourne", value: "RISTOURNE" },
                { text: "Non encaissé", value: "-" },
            ],
            filterMultiple: false,
            filteredValue: filteredInfo.modePaiement || null,
            // Filtrage serveur (cf. handleTableChange) — « Non encaissé » → statut UNPAID.
        },
        {
            title: "Date opération",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 130,
            render: (_, record) => <Text style={{ fontSize: 12 }}>{frenchDate(record.createdAt)}</Text>,
        },
        {
            title: "Date règlement",
            dataIndex: "dateReglement",
            key: "dateReglement",
            width: 130,
            render: (_, record) => (
                <Text style={{ fontSize: 12 }}>
                    {isPaid(record) ? frenchDate(record.dateReglement) : <Text type="secondary">—</Text>}
                </Text>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space size={4}>
                    {rightsService("HLA037") && record.numeroPolice && !isPaid(record) && (
                        <Tooltip title="Encaisser">
                            <Button
                                type="primary"
                                size="small"
                                icon={<CreditCardOutlined />}
                                onClick={() => encaisserContrat(record)}
                                style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Voir le contrat">
                        <Button
                            size="small"
                            icon={<SearchOutlined />}
                            onClick={() => navigate(`/details-contrat/${record.contratCodeId}`)}
                            style={{ borderRadius: 6 }}
                        />
                    </Tooltip>
                    {/* Annulation d'un encaissement : habilitation DÉDIÉE HLA102 (auparavant
                        HCO006, partagée avec la simple impression du reçu — deux actions de
                        sensibilité très différentes). La garde qui fait foi est côté serveur. */}
                    {rightsService("HLA102") && record.numeroPolice && isPaid(record) && (
                        <Tooltip title="Annuler l'encaissement">
                            <Button
                                danger
                                size="small"
                                icon={<span className="bx bx-x" style={{ fontSize: 14 }} />}
                                onClick={() => alertCancelReglement(record)}
                                style={{ borderRadius: 6 }}
                            />
                        </Tooltip>
                    )}
                    {rightsService("HCO006") && record.numeroPolice && isPaid(record) && (
                        <Tooltip title="Imprimer le reçu">
                            <Button
                                size="small"
                                icon={<FilePdfOutlined />}
                                onClick={() => openRecu(record)}
                                style={{ borderRadius: 6, background: "#1890ff", borderColor: "#1890ff", color: "#fff" }}
                            />
                        </Tooltip>
                    )}
                    {/* Compléter un encaissement PARTIEL (reste à payer) — HLA093. */}
                    {canEncaissementPartiel && record.numeroPolice
                        && (record.resteAPayer != null ? record.resteAPayer : Math.max((record.montant || 0) - (record.montantPaye != null ? record.montantPaye : (record.montant || 0)), 0)) > 0 && (
                            <Tooltip title="Compléter le paiement (reste à payer)">
                                <Button
                                    size="small"
                                    icon={<span className="bx bx-money" style={{ fontSize: 14 }} />}
                                    onClick={() => ouvrirCompletion(record)}
                                    style={{ borderRadius: 6, background: "#fa8c16", borderColor: "#fa8c16", color: "#fff" }}
                                />
                            </Tooltip>
                        )}
                </Space>
            ),
        },
    ];

    const alertReglement = (contractToPay) => {
        // Montant réellement encaissé = saisie partielle (HLA093) si < dû, sinon le dû total.
        const estPartiel = canEncaissementPartiel
            && montantEncaisse != null && contractToPay?.montant > 0 && montantEncaisse < contractToPay.montant;
        const montantAEncaisser = estPartiel ? montantEncaisse : contractToPay.montant;
        confirm({
            title: "Confirmer l'encaissement",
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <p>Encaisser le règlement de <strong>{displayNumbers(montantAEncaisser)} FCFA</strong> ?</p>
                    {estPartiel && (
                        <p style={{ color: "#d48806", fontSize: 12, margin: "4px 0" }}>
                            Encaissement partiel — dû : {displayNumbers(contractToPay.montant)} FCFA,
                            reste à payer : {displayNumbers(contractToPay.montant - montantEncaisse)} FCFA.
                        </p>
                    )}
                    <p style={{ color: "#888", fontSize: 12 }}>Toutes vos actions sont historisées.</p>
                </div>
            ),
            okText: "Confirmer",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementContrat(contractToPay); },
        });
    };

    const alertCancelReglement = (reglementToCancel) => {
        confirm({
            title: "Annuler cet encaissement ?",
            icon: <ExclamationCircleFilled />,
            content: "Cette action est irréversible. Toutes vos actions sont historisées.",
            okText: "Oui, annuler",
            okType: "danger",
            cancelText: "Retour",
            onOk() { cancelReglement(reglementToCancel); },
        });
    };

    const cancelReglement = (data) => {
        axiosInstance
            .get(`/api/reglement/cancel/${data.contratCodeId}/${data.codeId}`)
            .then(() => { toast.success("Règlement annulé avec succès"); window.location.reload(); })
            .catch(() => toast.error("Erreur lors de l'annulation du règlement"));
    };

    const reglementContrat = (data) => {
        // Encaissement PARTIEL (HLA093) : si un montant encaissé < dû a été saisi, on le transmet
        // en query param `montantPaye`. Sinon plein (le backend pose montantPaye = montant).
        const partiel = canEncaissementPartiel
            && montantEncaisse != null && data?.montant > 0 && montantEncaisse < data.montant;
        const suffixePartiel = partiel ? `?montantPaye=${Math.max(montantEncaisse, 0)}` : "";
        const url = useCompensationRistourne
            ? modePaiement === "ESPECES"
                ? `/api/reglement/compensation/${data.contratCodeId}/${data.codeId}`
                : `/api/reglement/compensation/${data.contratCodeId}/${data.codeId}/${modePaiement}/${numeroPaiement}/${banque}`
            : `/api/reglement/${data.contratCodeId}/${data.codeId}/${LOGGED_USER.codeId}/${modePaiement}/${numeroPaiement}/${banque ? banque : reference}/${data.montant}/${data.avenant}${suffixePartiel}`;

        axiosInstance.get(url)
            .then(() => { toast.success("Règlement effectué avec succès"); window.location.reload(); })
            .catch(() => toast.error("Erreur lors du règlement"));
    };

    const ouvrirCompletion = (record) => {
        setCompletReglement(record);
        setCompletMontant(null);
        setCompletModal(true);
    };

    const completerPaiement = () => {
        if (!completReglement?.codeId || !completMontant || completMontant <= 0) {
            toast.error("Saisir un montant à compléter (> 0)");
            return;
        }
        axiosInstance
            .put(`/api/reglement/${completReglement.codeId}/completer-paiement?montant=${completMontant}`)
            .then(() => { toast.success("Paiement complété"); window.location.reload(); })
            .catch(() => toast.error("Erreur lors de la complétion du paiement"));
    };

    const dateFiltering = (dates) => {
        // Le backend attend des dates au format ISO (yyyy-MM-dd) — on convertit depuis les objets
        // dayjs du RangePicker (la chaîne affichée DD/MM/YYYY n'est pas parsable côté serveur).
        if (dates && dates[0] && dates[1]) {
            setDateF([dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")]);
        } else {
            // Effacement → on revient au mois courant plutôt qu'à une requête non bornée (évite le 504).
            setDateF(DEFAULT_MONTH_STRINGS);
        }
        setCurrentPage(1);
    };

    const printRecu = createRef();
    const handleRecu = useReactToPrint({ content: () => printRecu.current });

    // Ouvre le reçu en chargeant les MÊMES entités complètes que /details-contrat
    // (le DTO de la liste /api/reglement est allégé : pas d'accessoires, dates effet/échéance,
    //  organismeCode ni objet contrat/avenant → reçu faux).
    const openRecu = (record) => {
        setReglement(record);
        setRecuContrat(record?.contrat || null);
        setRecuReglement(record);
        setModalOpen(true);
        if (!record?.contratCodeId) return;
        setLoadingRecu(true);
        axiosInstance
            .get(`/api/contrat/codeId/${record.contratCodeId}`)
            .then((res) => {
                const fullContrat = res.data;
                setRecuContrat(fullContrat);
                const police = fullContrat?.numeroPoliceP || record?.numeroPolice || record?.police;
                if (!police) { setLoadingRecu(false); return; }
                axiosInstance
                    .get(`/api/reglement/police/${police}`)
                    .then((resp) => {
                        const full = (resp.data || []).find((r) => r.codeId === record.codeId);
                        setRecuReglement(full || record);
                        setLoadingRecu(false);
                    })
                    .catch(() => setLoadingRecu(false));
            })
            .catch(() => setLoadingRecu(false));
    };

    const statCards = [
        {
            label: "Encaissements du jour",
            value: `${count?.numberOfReglementsToday ?? 0}`,
            sub: `${displayNumbers(Math.trunc(count?.totalAmountToday ?? 0))} FCFA`,
            color: "#1890ff",
            bg: "#e6f7ff",
            icon: "bx bx-calendar-check",
        },
        {
            label: "Encaissements du mois",
            value: `${count?.numberOfReglementsThisMonth ?? 0}`,
            sub: `${displayNumbers(Math.trunc(count?.totalAmountThisMonth ?? 0))} FCFA`,
            color: "#52c41a",
            bg: "#f6ffed",
            icon: "bx bx-line-chart",
        },
        {
            label: "Total encaissé",
            // Nombre ET montant issus du endpoint global de comptage (indépendant du filtre de date du
            // tableau, désormais borné au mois) → la carte reste un cumul global cohérent.
            value: `${count?.totalNumberOfReglements ?? 0}`,
            sub: `${displayNumbers(Math.trunc(count?.totalAmount ?? 0))} FCFA`,
            color: "#722ed1",
            bg: "#f9f0ff",
            icon: "bx bx-wallet",
        },
        {
            label: "Non encaissés",
            value: `${count?.numberOfNonCashedReglements ?? 0}`,
            sub: `${displayNumbers(Math.trunc(count?.totalAmountNonCashed ?? 0))} FCFA`,
            color: "#fa8c16",
            bg: "#fff7e6",
            icon: "bx bx-time-five",
            onClick: filtrerNonEncaisses,
            active: statutFilter === "UNPAID",
            hint: "Cliquer pour filtrer les non encaissés",
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"CAISSE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── Titre ── */}
                            <div style={{ marginBottom: 24 }}>
                                <Title level={4} style={{ margin: 0, color: "#242166" }}>
                                    <CreditCardOutlined style={{ marginRight: 8 }} />
                                    Encaissements
                                </Title>
                                <Text type="secondary">Suivi des règlements et encaissements</Text>
                            </div>

                            {/* ── Cartes de statistiques ── */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {statCards.map((s, i) => (
                                    <Col xs={24} sm={12} xl={6} key={i}>
                                        <Card
                                            onClick={s.onClick}
                                            title={s.hint}
                                            style={{
                                                borderRadius: 12,
                                                border: s.active ? `2px solid ${s.color}` : `1px solid ${s.color}30`,
                                                background: s.bg,
                                                boxShadow: s.active ? `0 0 0 3px ${s.color}25` : "0 2px 8px rgba(0,0,0,0.06)",
                                                cursor: s.onClick ? "pointer" : "default",
                                            }}
                                            bodyStyle={{ padding: "16px 20px" }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <Text style={{ color: "#666", fontSize: 12, display: "block", marginBottom: 4 }}>
                                                        {s.label}
                                                    </Text>
                                                    <Text strong style={{ fontSize: 22, color: s.color, display: "block" }}>
                                                        {s.value}
                                                    </Text>
                                                    <Text style={{ fontSize: 12, color: s.color }}>
                                                        {s.sub}
                                                    </Text>
                                                </div>
                                                <div style={{
                                                    width: 44, height: 44, borderRadius: 10,
                                                    background: `${s.color}20`,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <i className={s.icon} style={{ fontSize: 22, color: s.color }} />
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* ── Tableau ── */}
                            <Card
                                style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                                bodyStyle={{ padding: "16px 20px" }}
                            >
                                {/* Barre d'outils */}
                                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                                    <Col xs={24} sm={8}>
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                                            placeholder="Rechercher assuré, police, n° reçu..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            allowClear
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={10}>
                                        <ConfigProvider locale={frFR}>
                                            <RangePicker
                                                onChange={dateFiltering}
                                                value={dateF.length === 2 ? [dayjs(dateF[0]), dayjs(dateF[1])] : null}
                                                placeholder={["Date début", "Date fin"]}
                                                // Autorise le filtre jusqu'à la fin du mois courant afin de voir
                                                // les encaissements à venir du mois en cours.
                                                disabledDate={(current) => current && current > dayjs().endOf("month")}
                                                style={{ width: "100%", borderRadius: 8 }}
                                                format="DD/MM/YYYY"
                                            />
                                        </ConfigProvider>
                                    </Col>
                                    {/* Filtre par CAISSIER : la liste ET l'export le prennent en compte. */}
                                    <Col xs={24} sm={8}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Tous les caissiers"
                                            style={{ width: "100%" }}
                                            value={caissierFilter}
                                            onChange={(v) => {
                                                setCaissierFilter(v || null);
                                                setCurrentPage(1); // sinon page vide sur un périmètre réduit
                                            }}
                                            options={caissiers.map((c) => ({ value: c.codeId, label: c.nom }))}
                                        />
                                    </Col>
                                    <Col xs={24} sm={6} style={{ textAlign: "right" }}>
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            loading={exporting}
                                            style={{
                                                borderRadius: 8,
                                                background: "#217346",
                                                borderColor: "#217346",
                                                color: "#fff",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Exporter Excel
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Exports PDF : version « à plat » de l'export Excel, et état
                                    journalier groupé agence → caissier (avec sous-totaux). */}
                                <Row gutter={[12, 12]} style={{ marginTop: 12 }} justify="end">
                                    <Col>
                                        <Button
                                            icon={<FilePdfOutlined />}
                                            onClick={exportToPDF}
                                            loading={exportingPdf}
                                            style={{
                                                borderRadius: 8,
                                                background: "#b7261f",
                                                borderColor: "#b7261f",
                                                color: "#fff",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Exporter PDF
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Tooltip title="Encaissements du jour groupés par agence puis par caissier, avec sous-totaux et total général">
                                            <Button
                                                icon={<FilePdfOutlined />}
                                                onClick={genererEtatJournalier}
                                                loading={exportingEtat}
                                                style={{
                                                    borderRadius: 8,
                                                    background: "#242166",
                                                    borderColor: "#242166",
                                                    color: "#fff",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                État journalier
                                            </Button>
                                        </Tooltip>
                                    </Col>
                                </Row>

                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            rowKey={(record) => record.codeId || record.contratCodeId}
                                            columns={columns}
                                            dataSource={filteredDatas}
                                            onChange={handleTableChange}
                                            pagination={{
                                                current: currentPage,
                                                pageSize: pageSize,
                                                total: total,
                                                showSizeChanger: true,
                                                showTotal: (t) => `${t} règlements`,
                                                pageSizeOptions: ["10", "20", "50"],
                                            }}
                                            rowClassName={(record) =>
                                                isPaid(record) ? "" : "row-pending"
                                            }
                                            onRow={(record) => ({
                                                style: {
                                                    background: isPaid(record)
                                                        ? STATUS_COLORS.paid.bg
                                                        : STATUS_COLORS.pending.bg,
                                                    borderLeft: `3px solid ${isPaid(record)
                                                        ? STATUS_COLORS.paid.border
                                                        : STATUS_COLORS.pending.border}`,
                                                    transition: "all 0.2s",
                                                },
                                            })}
                                            size="middle"
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>

            {/* ── Modal aperçu reçu ── */}
            {reglement && (
                <Modal
                    title={
                        <Space>
                            <FilePdfOutlined />
                            <span>Reçu — {reglement?.avenant?.nom || reglement?.avenant}</span>
                        </Space>
                    }
                    centered
                    open={modalOpen}
                    onOk={() => setModalOpen(false)}
                    onCancel={() => setModalOpen(false)}
                    width={1000}
                    zIndex={2000}
                    footer={[
                        <Button key="close" onClick={() => setModalOpen(false)}>Fermer</Button>,
                        <Button
                            key="print"
                            type="primary"
                            icon={<FilePdfOutlined />}
                            onClick={handleRecu}
                            disabled={!rightsService("HCO008") || loadingRecu || !recuReglement}
                            style={{ background: "#52c41a", borderColor: "#52c41a" }}
                        >
                            Imprimer
                        </Button>,
                    ]}
                >
                    <div style={{ height: "70vh", overflowY: "auto" }}>
                        {loadingRecu ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <Spin tip="Chargement du reçu…" />
                            </div>
                        ) : (
                            <RecuCurrentFormat ref={printRecu} contrat={recuContrat} reglement={recuReglement} />
                        )}
                    </div>
                </Modal>
            )}

            {/* ── Modal mode de paiement ── */}
            <Modal
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>Mode de paiement</span>
                    </Space>
                }
                centered
                open={modalModePaiement}
                onOk={() => alertReglement(contractToPay)}
                onCancel={() => setModalModePaiement(false)}
                okText="Encaisser"
                cancelText="Annuler"
                okButtonProps={{ style: { background: "#52c41a", borderColor: "#52c41a" } }}
            >
                <Form form={modePaiementForm} layout="vertical">
                    {havingRistourne && (
                        <Form.Item name="useCompensation" label="Utiliser la compensation ristourne ?">
                            <Switch
                                checkedChildren="OUI"
                                unCheckedChildren="NON"
                                onChange={(checked) => setUseCompensationRistourne(checked)}
                                checked={useCompensationRistourne}
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="modePaiement"
                        label="Mode de paiement"
                        rules={[{ required: true, message: "Veuillez sélectionner un mode" }]}
                    >
                        <Select
                            onChange={(value) => setModePaiement(value)}
                            placeholder="Sélectionner le mode de paiement"
                            style={{ borderRadius: 8 }}
                        >
                            <Option value="ESPECES">Espèces</Option>
                            <Option value="BANQUE">Banque (Chèque)</Option>
                            <Option value="VIREMENT">Banque (Virement)</Option>
                            <Option value="MOBILE_MONEY">Mixx By Yas / Moov Money</Option>
                            {contractToPay.montant < 0 && <Option value="RISTOURNE">Ristourne</Option>}
                        </Select>
                    </Form.Item>
                    {/* ENCAISSEMENT PARTIEL (HLA093) : montant réellement encaissé ≤ dû. Plein par défaut. */}
                    {canEncaissementPartiel && contractToPay?.montant > 0 && (
                        <Form.Item
                            label="Montant encaissé"
                            help={`Dû : ${Number(contractToPay?.montant || 0).toLocaleString("fr-FR")} F. Saisir un montant inférieur = encaissement partiel (le reste à payer sera suivi en caisse).`}
                        >
                            <InputNumber
                                min={0}
                                max={contractToPay?.montant}
                                value={montantEncaisse}
                                onChange={(v) => setMontantEncaisse(v == null ? null : Number(v))}
                                style={{ width: "100%", borderRadius: 8 }}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                parser={(v) => v.replace(/\s/g, "")}
                                addonAfter="FCFA"
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY") && (
                        <Form.Item
                            name="numeroPaiement"
                            label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro Flooz / TMoney"}
                            rules={[{ required: true, message: "Veuillez renseigner ce champ" }]}
                        >
                            <Input
                                placeholder={modePaiement === "BANQUE" ? "Numéro du chèque" : "Numéro de téléphone"}
                                onChange={(e) => setNumeroPaiement(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="banque"
                            label="Banque"
                            rules={[{ required: true, message: "Veuillez sélectionner la banque" }]}
                        >
                            <Select
                                onChange={(value) => setBanque(value)}
                                placeholder="Sélectionner la banque"
                                style={{ borderRadius: 8 }}
                            >
                                {banques.map((b) => (
                                    <Option key={b.parametreId} value={b.libelleParametre}>
                                        {b.libelleParametre}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {(modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="referencePaiement"
                            label="Référence du paiement"
                            rules={[{ required: true, message: "Veuillez renseigner la référence" }]}
                        >
                            <Input
                                placeholder="Numéro de référence"
                                onChange={(e) => setReference(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {modePaiement === "VIREMENT" && (
                        <Form.Item
                            name="dateVirement"
                            label="Date du virement"
                            rules={[{ required: true, message: "Veuillez sélectionner la date" }]}
                        >
                            <ConfigProvider locale={frFR}>
                                <DatePicker
                                    placeholder="Date du virement"
                                    onChange={(_, value) => setDateVirement(value)}
                                    style={{ width: "100%", borderRadius: 8 }}
                                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                                />
                            </ConfigProvider>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                title="Compléter le paiement"
                open={completModal}
                onOk={completerPaiement}
                onCancel={() => setCompletModal(false)}
                okText="Compléter"
                cancelText="Annuler"
                okButtonProps={{ style: { background: "#fa8c16", borderColor: "#fa8c16" } }}
            >
                <p>
                    Reste à payer :{" "}
                    <strong>
                        {displayNumbers(Math.trunc(
                            completReglement?.resteAPayer != null
                                ? completReglement.resteAPayer
                                : Math.max((completReglement?.montant || 0) - (completReglement?.montantPaye != null ? completReglement.montantPaye : (completReglement?.montant || 0)), 0)
                        ))} FCFA
                    </strong>
                </p>
                <InputNumber
                    min={0}
                    max={
                        completReglement?.resteAPayer != null
                            ? completReglement.resteAPayer
                            : Math.max((completReglement?.montant || 0) - (completReglement?.montantPaye != null ? completReglement.montantPaye : (completReglement?.montant || 0)), 0)
                    }
                    placeholder="Montant encaissé maintenant"
                    value={completMontant}
                    onChange={(v) => setCompletMontant(v == null ? null : Number(v))}
                    style={{ width: "100%", borderRadius: 8 }}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                    parser={(v) => v.replace(/\s/g, "")}
                    addonAfter="FCFA"
                />
            </Modal>

            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default Encaissements;
